import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../api';
import './TrustCenterSettings.css';
import { getOrgPermissions } from '../../utils/permissions';
import TrustCenterAuthoring from './TrustCenterAuthoring';

function TrustCenterSettings() {
  const { t } = useTranslation();
  const { canManageTrustCenter } = getOrgPermissions();
  const [, setSettings] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [saved,    setSaved]    = useState(false);
  const [form,     setForm]     = useState({
    slug: '', isPublic: false, showReadinessScore: true,
    certifications: '', customMessage: '',
  });

  useEffect(() => {
    axiosInstance.get('/trust-center')
      .then(res => {
        const d = res.data;
        setSettings(d);
        setForm({
          slug:               d.slug || '',
          isPublic:           !!d.isPublic,
          showReadinessScore: d.showReadinessScore !== false,
          certifications:     d.certifications || '',
          customMessage:      d.customMessage || '',
        });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaved(false);
    try {
      const res = await axiosInstance.put('/trust-center', form);
      setSettings(res.data);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch {
      alert(t('TrustCenterSettings.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const publicUrl = form.slug
    ? `${window.location.origin}${window.location.pathname}#/trust/${form.slug}`
    : null;

  if (loading) return (
    <div className="tc-page">
      <div className="tc-spinner-wrap"><div className="tc-spinner" /></div>
    </div>
  );

  return (
    <div className="tc-page">
      <div className="tc-header">
        <div>
          <h2 className="tc-title"><i className="fas fa-globe" /> {t('TrustCenterSettings.title')}</h2>
          <p className="tc-subtitle">
            {t('TrustCenterSettings.subtitle')}
          </p>
        </div>
        {form.isPublic && publicUrl && (
          <a className="tc-view-btn" href={publicUrl} target="_blank" rel="noreferrer">
            <i className="fas fa-external-link-alt" /> {t('TrustCenterSettings.viewPublicPage')}
          </a>
        )}
      </div>

      <div className="tc-card">
        <div className="tc-section-label">{t('TrustCenterSettings.visibility')}</div>

        <label className="tc-toggle-row">
          <div>
            <div className="tc-toggle-title">{t('TrustCenterSettings.makePublicTitle')}</div>
            <div className="tc-toggle-desc">{t('TrustCenterSettings.makePublicDesc')}</div>
          </div>
          <div
            className={`tc-toggle ${form.isPublic ? 'tc-toggle-on' : ''}`}
            onClick={() => canManageTrustCenter && setForm(f => ({ ...f, isPublic: !f.isPublic }))}
            style={canManageTrustCenter ? {} : { opacity: 0.5, cursor: 'not-allowed' }}
          >
            <div className="tc-toggle-thumb" />
          </div>
        </label>

        <label className="tc-toggle-row">
          <div>
            <div className="tc-toggle-title">{t('TrustCenterSettings.showReadinessTitle')}</div>
            <div className="tc-toggle-desc">{t('TrustCenterSettings.showReadinessDesc')}</div>
          </div>
          <div
            className={`tc-toggle ${form.showReadinessScore ? 'tc-toggle-on' : ''}`}
            onClick={() => canManageTrustCenter && setForm(f => ({ ...f, showReadinessScore: !f.showReadinessScore }))}
            style={canManageTrustCenter ? {} : { opacity: 0.5, cursor: 'not-allowed' }}
          >
            <div className="tc-toggle-thumb" />
          </div>
        </label>
      </div>

      <div className="tc-card">
        <div className="tc-section-label">{t('TrustCenterSettings.publicUrl')}</div>
        <div className="tc-field">
          <label className="tc-label">{t('TrustCenterSettings.urlSlug')}</label>
          <div className="tc-slug-wrap">
            <span className="tc-slug-prefix">/trust/</span>
            <input
              className="tc-input tc-slug-input"
              placeholder={t('TrustCenterSettings.slugPlaceholder')}
              value={form.slug}
              onChange={e => setForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))}
            />
          </div>
          {publicUrl && (
            <div className="tc-url-preview">
              <i className="fas fa-link" /> {publicUrl}
            </div>
          )}
        </div>
      </div>

      <div className="tc-card">
        <div className="tc-section-label">{t('TrustCenterSettings.content')}</div>

        <div className="tc-field">
          <label className="tc-label">{t('TrustCenterSettings.certificationsLabel')}</label>
          <input
            className="tc-input"
            placeholder={t('TrustCenterSettings.certificationsPlaceholder')}
            value={form.certifications}
            onChange={e => setForm(f => ({ ...f, certifications: e.target.value }))}
          />
        </div>

        <div className="tc-field">
          <label className="tc-label">{t('TrustCenterSettings.customMessageLabel')}</label>
          <textarea
            className="tc-input tc-textarea"
            placeholder={t('TrustCenterSettings.customMessagePlaceholder')}
            rows={4}
            value={form.customMessage}
            onChange={e => setForm(f => ({ ...f, customMessage: e.target.value }))}
          />
        </div>
      </div>

      <div className="tc-footer">
        {saved && (
          <span className="tc-saved">
            <i className="fas fa-check-circle" /> {t('TrustCenterSettings.savedSuccessfully')}
          </span>
        )}
        {!canManageTrustCenter && (
          <span className="tc-readonly-notice">
            <i className="fas fa-lock" /> {t('TrustCenterSettings.readOnlyNotice')}
          </span>
        )}
        {canManageTrustCenter && (
          <button className="tc-save-btn" onClick={handleSave} disabled={saving}>
            {saving
              ? <><i className="fas fa-spinner fa-spin" /> {t('TrustCenterSettings.saving')}</>
              : <><i className="fas fa-save" /> {t('TrustCenterSettings.saveSettings')}</>}
          </button>
        )}
      </div>

      {/* Content authoring: subprocessors, documents, and the access-request inbox */}
      <TrustCenterAuthoring canManage={canManageTrustCenter} />
    </div>
  );
}

export default TrustCenterSettings;
